import React from 'react';
import { Image, Link } from '@/components';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { generatePersonSlug } from '@/lib/utils';

interface ImageWithAlt {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
}

interface PersonRole {
  name: string;
}

interface PersonInfo {
  firstName: string;
  lastName: string;
  nameOrder: 'firstLast' | 'lastFirst';
  language: string;
  photo: ImageWithAlt;
  personRole: PersonRole;
  shortBiography?: string;
  biography?: PortableTextBlockType;
  cahHeroType?: string;
}

const cahHeroTypeImage = (cahHeroType?: string) => {
  if (!cahHeroType) return undefined;
  switch (cahHeroType) {
    case 'communityCaptain':
      return '/images/community-captain.png';
    case 'arborAvenger':
      return '/images/arbor-avenger.png';
    case 'pollinatorPatrol':
      return '/images/pollinator-patrol.png';
    case 'waterWarrior':
      return '/images/water-warrior.png';
    case 'mightyMeteorologist':
      return '/images/mighty-meteorologist.png';
    default:
      return '';
  }
};

interface PersonProps {
  person: PersonInfo;
  isLinkToPersonPage: boolean;
}

const Person: React.FC<PersonProps> = ({ person, isLinkToPersonPage }) => {
  const {
    firstName,
    lastName,
    nameOrder,
    photo,
    personRole,
    shortBiography,
    biography,
    cahHeroType,
    language,
  } = person;

  const fullName =
    nameOrder === 'lastFirst' ? `${lastName} ${firstName}` : `${firstName} ${lastName}`;

  const slug = generatePersonSlug(firstName, lastName, nameOrder);

  return (
    <div>
      <div className='relative mx-auto mb-4 w-max max-w-full'>
        <Image
          className='w-[330px] rounded-[20px]'
          image={photo.image}
          alt={`${photo?.alt || `${firstName} ${lastName}`}`}
          width={330}
        />
        {cahHeroType && (
          <div className='absolute -bottom-5 right-2.5 h-28 w-28'>
            <img
              className='h-28 w-28 object-contain'
              src={cahHeroTypeImage(cahHeroType)}
              alt={cahHeroType}
            />
          </div>
        )}
      </div>
      {isLinkToPersonPage && biography ? (
        <Link
          internalLink={{
            slug: { current: slug },
            language: language,
            _type: 'person',
          }}
          className='squiggly-link-trigger group flex flex-col'
        >
          <h3 className='mb-1.5'>
            <div className='squiggly-link text-3xl font-semibold leading-9 tracking-normal'>
              {fullName}
            </div>
          </h3>
        </Link>
      ) : (
        <h3 className='mb-1.5 text-3xl font-semibold leading-9 tracking-normal'>{fullName}</h3>
      )}

      <p className='mb-2.5 text-lg font-semibold text-black'>{personRole.name}</p>

      {shortBiography && (
        <>
          <p className='leading-xl border-t border-dotted border-black pt-2.5 text-left font-sans text-base font-normal tracking-normal'>
            favorite childhood activity:
          </p>
          <p className='leading-xl text-left font-sans text-base font-light tracking-normal'>
            {shortBiography}
          </p>
        </>
      )}
    </div>
  );
};

export default Person;
